import { HelpCircle, User as UserIcon, LogOut, Building2, ChevronDown } from '@/lib/icons';
import { Navigation, NavBrand, AccountMenu, useSidebar, type AccountMenuItem } from 'prosci-ui';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useMasquerade } from '@/hooks/use-masquerade';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { sessionAuthService } from '@/services/session-auth-service';
import { accountSettingsService } from '@/services/account-settings-service';
import { useQuery } from '@tanstack/react-query';

function getInitials(name?: string) {
  if (!name) return 'SS';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

function getAdminRoleLabel(type?: string, level?: string): string {
  if (type === 'license_admin') {
    return 'License Administrator';
  }
  if (type === 'prosci_admin' && level) {
    const roleMap: Record<string, string> = {
      super: 'Super Admin',
      admin: 'Administrative User',
      data: 'Customer Editor',
      affiliate: 'Affiliate',
      affiliate_admin: 'Affiliate + Admin',
    };
    return roleMap[level] || level;
  }
  if (type === 'prosci_admin') {
    return 'Prosci Admin';
  }
  return type || 'Unknown';
}

export function TopNavigation() {
  const { data: currentAdmin } = useCurrentUser();
  const { user: auth0User, logout } = useAuth0();
  const { toggleSidebar } = useSidebar();
  const { isMasquerading } = useMasquerade();
  const navigate = useNavigate();

  const helpUrl = import.meta.env.VITE_HELP_URL;
  const portalUrl = import.meta.env.VITE_PORTAL_URL || 'https://portal.prosci.com';
  const termsHref =
    import.meta.env.VITE_TERMS_CONDITIONS_LINK ||
    'https://help.prosci.com/en/collections/17098906-terms-and-privacy';
  const privacyHref = import.meta.env.VITE_PRIVACY_POLICY_LINK || 'https://www.prosci.com/privacy-policy';

  // Fetch account settings to get company logo for license admins
  const accountId = currentAdmin?.organization_id;
  const { data: accountSettings } = useQuery({
    queryKey: ['account-settings', accountId],
    queryFn: () => accountSettingsService.getSettings(accountId!),
    enabled: !!accountId && currentAdmin?.type === 'license_admin',
  });

  const isProsciAdmin = currentAdmin?.type === 'prosci_admin';

  // Use currentAdmin if available, otherwise fall back to Auth0 user
  const user = currentAdmin
    ? { name: currentAdmin.name, email: currentAdmin.email }
    : { name: auth0User?.name, email: auth0User?.email };
  const roleLabel = currentAdmin?.type ? getAdminRoleLabel(currentAdmin.type, currentAdmin.level) : undefined;

  const handleEditProfile = () => {
    // License admins share session with portal - just redirect, no session clearing needed.
    // The shared cookie keeps them authenticated on both sides.
    window.location.href = `${portalUrl}/profile`;
  };

  const handleReturnToPortal = () => {
    window.location.href = portalUrl;
  };

  const handleLogout = () => {
    const isLicenseAdmin = currentAdmin?.type === 'license_admin';

    if (isLicenseAdmin) {
      // License admins share session with portal - portal logout clears the
      // shared cookie and handles Auth0 logout.
      window.location.href = `${portalUrl}/logout`;
    } else {
      sessionAuthService.clearSession({ skipEvent: true });
      logout({ logoutParams: { returnTo: window.location.origin } });
    }
  };

  // Prosci admins don't need Manage Organization, Edit Profile, or Return to Portal.
  // Icon wrappers vs. AccountMenuItem's icon type disagree across the prosci-ui/app @types/react boundary.
  const menuItems: AccountMenuItem[] = isProsciAdmin
    ? []
    : [
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        { name: 'Manage Organization', icon: Building2 as any, onSelect: () => navigate('/users') },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        { name: 'Edit Profile', icon: UserIcon as any, onSelect: handleEditProfile },
      ];
  const secondaryItems: AccountMenuItem[] = [
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    { name: 'Help', icon: HelpCircle as any, external: true, href: helpUrl },
    ...(isProsciAdmin
      ? []
      : [
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          { name: 'Return to Portal', icon: LogOut as any, onSelect: handleReturnToPortal },
        ]),
  ];

  const trigger = accountSettings?.company_logo_url ? (
    <button
      type="button"
      aria-label="Account menu"
      className="flex shrink-0 items-center gap-1.5 rounded-full py-1 pl-1 pr-2 text-white transition-colors duration-150 hover:bg-white/10"
    >
      <span className="flex h-8 max-w-32 items-center justify-center rounded-full bg-white px-2">
        <img
          src={accountSettings.company_logo_url}
          alt={accountSettings.company_name || 'Company logo'}
          className="max-h-6 max-w-full object-contain"
        />
      </span>
      <ChevronDown className="h-4 w-4" />
    </button>
  ) : undefined;

  return (
    <Navigation
      brand={<NavBrand icon={<img src="/prosci.svg" alt="Prosci" className="h-6 w-6" />} name="Portal Admin" href="/users" />}
      onToggleSidebar={toggleSidebar}
    >
      <AccountMenu
        user={{ name: user.name || '', email: user.email || '', initials: getInitials(user.name), role: roleLabel }}
        menuItems={menuItems}
        secondaryItems={secondaryItems}
        onLogout={isMasquerading ? undefined : handleLogout}
        logoutLabel="Logout"
        termsHref={termsHref}
        privacyHref={privacyHref}
        trigger={trigger}
      />
    </Navigation>
  );
}
